import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraInput, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { money } from './ratesView';
import { usePricingWrite } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
export function SetRateDialog({ scope, detail: incomingDetail, token: incomingToken, access, initialSource = 'suggestion', onClose, onReload }) {
    const [detail] = useState(incomingDetail);
    const [token] = useState(incomingToken);
    const row = detail.row;
    const [source, setSource] = useState(initialSource);
    const [amount, setAmount] = useState('');
    const [override, setOverride] = useState('');
    const [observationId, setObservationId] = useState('');
    const [reason, setReason] = useState('');
    const write = usePricingWrite((basis)=>projectDataClient.setPricingRate({
            projectId: scope.projectId,
            scenarioId: scope.scenarioId,
            periodKey: scope.period,
            categoryId: row?.categoryId,
            basis,
            suggestionToken: row?.suggested?.token,
            expectedUpdatedAt: token,
            observationId,
            observationVersion: detail.observations.find((o)=>o.observationId === observationId)?.version,
            amount: basis === 'manual' ? amount : basis === 'observation' ? override : '',
            reason
        }));
    const observation = detail.observations.find((o)=>o.observationId === observationId);
    const resulting = source === 'manual' ? amount : source === 'observation' ? override || observation?.amount : row?.suggested?.amount;
    const reasonRequired = source === 'manual' || !!override || Number(resulting) !== Number(row?.suggested?.amount);
    const valid = Number(resulting) > 0 && /^\d+(\.\d{1,2})?$/.test(resulting || '') && (!reasonRequired || !!reason.trim()) && (source !== 'observation' || access && !!observation?.selectable);
    const stale = write.error && [
        Code.Aborted,
        Code.FailedPrecondition
    ].includes(ConnectError.from(write.error).code);
    const save = async (basis)=>{
        try {
            await write.mutateAsync(basis);
            onClose();
        } catch  {
        /* inline error */ }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (open)=>{
            if (!open && !write.isPending) onClose();
        },
        title: `Set rate · ${row?.title} · ${scope.period}`,
        testId: T.setRateDialog,
        footer: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "destructive",
                    "data-testid": T.clearRate,
                    disabled: !reason.trim() || write.isPending || !!stale,
                    onClick: ()=>save('clear'),
                    children: "Clear rate"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: onClose,
                    disabled: write.isPending,
                    children: "Cancel"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.save,
                    disabled: !valid || write.isPending || !!stale,
                    onClick: ()=>save(source),
                    children: write.isPending ? 'Saving…' : 'Save'
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Rate source",
                    testId: T.rateSource,
                    value: source,
                    onValueChange: (v)=>{
                        setSource(v);
                        setOverride('');
                    },
                    options: [
                        {
                            value: 'suggestion',
                            label: 'Use suggestion',
                            disabled: !row?.suggested?.amount
                        },
                        {
                            value: 'observation',
                            label: 'Pick an observation',
                            disabled: !access
                        },
                        {
                            value: 'manual',
                            label: 'Enter manually'
                        }
                    ]
                }),
                source === 'suggestion' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Suggested rate",
                            children: money(row?.suggested?.amount)
                        }),
                        row?.suggested?.requiresChoice && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "warning",
                            children: "This is a ceiling, not a price. Choose deliberately."
                        })
                    ]
                }),
                source === 'observation' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDataTable, {
                            getRowLabel: (o)=>`${o.companyName} ${o.typeLabel}`,
                            rows: detail.observations,
                            rowKey: (o)=>`${o.observationId}:${o.version}`,
                            columns: [
                                {
                                    id: 'source',
                                    header: 'Observation',
                                    cell: (o)=>`${o.companyName} · ${o.typeLabel} · ${o.sourceLabel}`
                                },
                                {
                                    id: 'amount',
                                    header: 'Rate',
                                    cell: (o)=>money(o.amount)
                                }
                            ],
                            onRowClick: (o)=>{
                                if (o.selectable) setObservationId(o.observationId);
                            },
                            isRowSelected: (o)=>o.observationId === observationId
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Override amount",
                            htmlFor: "rate-override",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "rate-override",
                                inputMode: "decimal",
                                value: override,
                                onChange: (e)=>setOverride(e.target.value)
                            })
                        })
                    ]
                }),
                source === 'manual' && /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Rate ($/hr)",
                    htmlFor: "rate-amount",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "rate-amount",
                        "data-testid": T.manualAmount,
                        inputMode: "decimal",
                        value: amount,
                        onChange: (e)=>setAmount(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why",
                    htmlFor: "rate-reason",
                    required: reasonRequired,
                    hint: `Suggested is ${money(row?.suggested?.amount)} (${row?.suggested?.ruleLabel || 'no suggestion'}). Say why you're using a different rate.`,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        id: "rate-reason",
                        "data-testid": T.reason,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value)
                    })
                }),
                write.error && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    children: [
                        stale ? 'This changed since you opened it. Reload to see the latest.' : write.error.message,
                        stale && /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "xs",
                            onClick: async ()=>{
                                await onReload();
                                onClose();
                            },
                            children: "Reload"
                        })
                    ]
                })
            ]
        })
    });
}
